import { sanitizeStoreCoordinates } from '../../utils/storeUtils';

export interface ReceiptLocationInput {
  storeName?: string | null;
  storeAddress?: string | null;
  storeAddressNormalized?: string | null;
  lat?: number;
  lng?: number;
}

// No name-only branch guessing: a chain name or merchant phone is not a location.
export function receiptHasAddress(value?: string | null): boolean {
  const address = (value || '').replace(/\+?\d[\d ().-]{8,}\d/g, '')
    .replace(/\b(?:phone|tel(?:ephone)?|fax)\s*:?/gi, '').trim();
  return address.length > 4 && /\p{L}/u.test(address) &&
    !/^(?:store|branch|location)\s*#?\s*\d+$/i.test(address);
}

export async function resolveReceiptLocation(input: ReceiptLocationInput, lookup: {
  directory: (name: string, address: string) => Promise<any>;
  geocode: (name: string, address: string, normalized?: string | null) => Promise<any>;
}) {
  const name = input.storeName?.trim();
  const address = receiptHasAddress(input.storeAddress) ? input.storeAddress!.trim() : undefined;
  // A model-generated normalized address alone cannot identify a missing branch.
  if (!name || !address) return null;
  const coords = input.lat != null && input.lng != null
    ? sanitizeStoreCoordinates({ lat: input.lat, lng: input.lng }) : undefined;
  if (coords) return { name, address, coordinates: coords };
  try {
    const known = await lookup.directory(name, address);
    const coordinates = known && sanitizeStoreCoordinates({ lat: known.lat, lng: known.lng });
    if (coordinates) return { name: known.name || name, address: known.address || address, coordinates };
  } catch { /* a directory outage can still be answered by geocoding */ }
  try {
    const geo = await lookup.geocode(name, address, input.storeAddressNormalized);
    const coordinates = geo && sanitizeStoreCoordinates({ lat: geo.lat, lng: geo.lng });
    if (coordinates) return { name, address: geo.address || address, coordinates, url: geo.url };
  } catch { /* caller opens the picker or leaves the background draft for review */ }
  return null;
}
